import { useEffect, useRef, useCallback, useState } from "react";
import React from "react";

const TRAIL_COUNT = 8;
const BASE_SIZE = 6;
const BASE_OPACITY = 0.35;

/**
 * useCursorTrail — Renders a glowing dot trail following the cursor.
 * Disabled on touch devices and when prefers-reduced-motion is set.
 * @param {{ color?: string, enabled?: boolean }} opts
 * @returns {() => JSX.Element|null} CursorTrailLayer component
 */
export function useCursorTrail({ color = "#818cf8", enabled = true } = {}) {
  const dotsRef = useRef([]);
  const positions = useRef(Array.from({ length: TRAIL_COUNT }, () => ({ x: 0, y: 0 })));
  const mouseRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const containerRef = useRef(null);
  const [canRun, setCanRun] = useState(false);

  useEffect(() => {
    const hover = window.matchMedia("(hover: hover)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setCanRun(hover && !reduced && enabled);
  }, [enabled]);

  useEffect(() => {
    if (!canRun) return;

    const handleMouse = (e) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };
    window.addEventListener("mousemove", handleMouse, { passive: true });

    const animate = () => {
      const dots = dotsRef.current;
      const pos = positions.current;
      const mouse = mouseRef.current;

      for (let i = 0; i < TRAIL_COUNT; i++) {
        const target = i === 0 ? mouse : pos[i - 1];
        const factor = 0.18 - i * 0.015;
        pos[i].x += (target.x - pos[i].x) * factor;
        pos[i].y += (target.y - pos[i].y) * factor;

        if (dots[i]) {
          dots[i].style.transform = `translate(${pos[i].x}px, ${pos[i].y}px)`;
        }
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("mousemove", handleMouse);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [canRun]);

  const CursorTrailLayer = useCallback(() => {
    if (!canRun) return null;

    return (
      <div
        ref={containerRef}
        style={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 99999,
        }}
      >
        {Array.from({ length: TRAIL_COUNT }, (_, i) => {
          const size = BASE_SIZE - i * 0.5;
          const opacity = BASE_OPACITY - i * 0.035;
          return (
            <div
              key={i}
              ref={(el) => { dotsRef.current[i] = el; }}
              style={{
                position: "absolute", top: -size / 2, left: -size / 2,
                width: size, height: size, borderRadius: "50%",
                background: color, opacity,
                boxShadow: `0 0 ${6 - i * 0.5}px ${color}`,
                willChange: "transform",
                transition: "opacity 0.3s",
              }}
            />
          );
        })}
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);

  return CursorTrailLayer;
}
